import React, { useEffect, useState } from 'react';
import { Loader2, RotateCcw } from 'lucide-react';
import { toast } from 'react-toastify';
import settingsService from '../../services/settings.service';
import { getError } from '../../utils';
import { cn } from '../../lib/utils';
import RichHtmlEditor from '../RichHtmlEditor/RichHtmlEditor';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

/**
 * Admin editor for system emails (subject + HTML body) stored in GotaSettings.emailTemplates.
 * Bodies are Handlebars templates rendered inside the shared email layout (logo + footer).
 */
export default function NotificationsConfig() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await settingsService.getEmailTemplates();
        setTemplates(Array.isArray(data) ? data : []);
        setSelectedKey(data?.[0]?.key ?? null);
      } catch (err) {
        toast.error(getError(err));
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  const selected = templates.find(t => t.key === selectedKey);

  const updateSelected = patch =>
    setTemplates(prev => prev.map(t => (t.key === selectedKey ? { ...t, ...patch } : t)));

  const restoreDefault = () =>
    updateSelected({ subject: selected.defaultSubject, bodyHtml: selected.defaultBodyHtml });

  const copyVariable = async variable => {
    const text = `{{${variable}}}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.info(`${text} copiado`);
    } catch {
      toast.info(text);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const data = await settingsService.updateEmailTemplates(
        templates.map(({ key, subject, bodyHtml }) => ({ key, subject, bodyHtml }))
      );
      setTemplates(data);
      toast.success('Notificaciones guardadas');
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className='text-center py-4'>
        <Loader2 className='h-4 w-4 animate-spin' />
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap gap-2'>
        {templates.map(t => (
          <button
            key={t.key}
            type='button'
            onClick={() => setSelectedKey(t.key)}
            className={cn(
              'rounded-md border px-3 py-1.5 text-sm transition-colors',
              t.key === selectedKey
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-background text-foreground hover:bg-muted'
            )}
          >
            {t.name}
          </button>
        ))}
      </div>

      {selected && (
        <div className='space-y-4'>
          <p className='text-sm text-muted-foreground'>{selected.description}</p>

          <div className='space-y-1.5'>
            <label className='block text-sm font-medium text-foreground'>Asunto</label>
            <Input
              value={selected.subject}
              onChange={e => updateSelected({ subject: e.target.value })}
            />
          </div>

          <div className='space-y-1.5'>
            <label className='block text-sm font-medium text-foreground'>Cuerpo del correo</label>
            <RichHtmlEditor
              value={selected.bodyHtml}
              onChange={bodyHtml => updateSelected({ bodyHtml })}
              aria-label='Cuerpo del correo'
              minHeight={280}
            />
          </div>

          <div className='rounded-md border border-border bg-muted/40 p-3 space-y-2'>
            <p className='text-xs text-muted-foreground'>
              Variables disponibles (click para copiar). Se pueden usar en el asunto y en el cuerpo.
              Para un botón con los colores de la marca usá{' '}
              <code className='text-foreground'>{'{{button url "Texto del botón"}}'}</code>.
            </p>
            <div className='flex flex-wrap gap-1.5'>
              {selected.variables.map(v => (
                <button
                  key={v}
                  type='button'
                  onClick={() => void copyVariable(v)}
                  className='rounded border border-border bg-background px-2 py-0.5 font-mono text-xs text-foreground hover:bg-muted'
                >
                  {`{{${v}}}`}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className='flex flex-wrap gap-2'>
        <Button type='button' disabled={saving} onClick={() => void handleSave()}>
          {saving ? (
            <>
              <Loader2 className='h-4 w-4 animate-spin mr-2' />
              Guardando...
            </>
          ) : (
            'Guardar'
          )}
        </Button>
        {selected && (
          <Button type='button' variant='outline' disabled={saving} onClick={restoreDefault}>
            <RotateCcw className='h-4 w-4 mr-2' />
            Restaurar predeterminado
          </Button>
        )}
      </div>
    </div>
  );
}
